import Keycloak from "keycloak-js";
import { ref } from "vue";
import { useActivityTracking } from "../composables/auth/useActivityTracking";

let keycloak: Keycloak | null = null;
let keycloakInitialized = false;
// eslint-disable-next-line import/no-mutable-exports
export let isAuthenticated: boolean = false;
let tokenCheckInterval: NodeJS.Timeout | null = null;
let autoRefreshInterval: NodeJS.Timeout | null = null;
let activityTracking: ReturnType<typeof useActivityTracking> | null = null;
let isRefreshing = false;
/** Prevents concurrent logOut() invocations (timer + activity tracker racing). */
let isLoggingOut = false;
/**
 * Optional callback registered by the Nuxt app (useAuth) to reset reactive
 * auth state synchronously when a timer-triggered logout fires. This ensures
 * the auth middleware can redirect correctly even if keycloak.logout() fails
 * to navigate the browser.
 */
let logoutCallback: (() => void) | null = null;

/**
 * Register a callback that is called synchronously at the start of logOut().
 * Used by useAuth to keep Nuxt reactive state in sync with module-level state.
 * Safe to call multiple times — only the last registration is kept.
 */
export function registerLogoutCallback(callback: () => void): void {
  logoutCallback = callback;
}

// Configuration from environment variables
let autoRefreshEnabled = true;
let autoRefreshCheckInterval = 15000; // Default: 15 seconds
let autoRefreshTimeLeft = 30; // Default: 30 seconds before expiry
let activityTrackingEnabled = true;

// Reactive state for UI
export const accessTokenExpiration = ref<number | null>(null);
export const refreshTokenExpiration = ref<number | null>(null);
export const accessTokenTimeLeft = ref<string>("00:00");
export const refreshTokenTimeLeft = ref<string>("00:00");

// Initialize Keycloak
export async function initAuth(
  onLoad: "login-required" | "check-sso" = "login-required",
  runtimeConfig: {
    kcUrl: string;
    kcRealm: string;
    kcClientId: string;
    autoRefreshEnabled?: boolean;
    autoRefreshCheckInterval?: number;
    autoRefreshTimeLeft?: number;
    activityTrackingEnabled?: boolean;
  },
): Promise<boolean> {
  // Read configuration
  autoRefreshEnabled = runtimeConfig.autoRefreshEnabled ?? true;
  autoRefreshCheckInterval = runtimeConfig.autoRefreshCheckInterval ?? 15000;
  autoRefreshTimeLeft = runtimeConfig.autoRefreshTimeLeft ?? 30;
  activityTrackingEnabled = runtimeConfig.activityTrackingEnabled ?? true;

  if (!keycloak) {
    // Initialize Keycloak instance dynamically
    keycloak = new Keycloak({
      url: runtimeConfig.kcUrl,
      realm: runtimeConfig.kcRealm,
      clientId: runtimeConfig.kcClientId,
    });
  }

  if (keycloakInitialized) {
    return isAuthenticated;
  }

  try {
    isAuthenticated = await keycloak.init({
      onLoad,
      checkLoginIframe: false,
    });
    keycloakInitialized = true;

    if (isAuthenticated) {
      startTokenExpirationTracking();

      // Start activity tracking if enabled
      // Activity tracking handles token refresh, so we don't need separate auto-refresh
      if (activityTrackingEnabled) {
        startActivityTracking();
      } else if (autoRefreshEnabled) {
        // Only use timer-based auto-refresh if activity tracking is disabled
        startAutoTokenRefresh();
      }
    }

    return isAuthenticated;
  } catch (error) {
    console.error("Keycloak initialization failed:", error);
    return false;
  }
}

// Check if the user is authenticated
export function isLogged(): boolean {
  return isAuthenticated;
}

// Get the JWT token
export function getToken(): string | null {
  return keycloak?.token || null;
}

// Refresh the token automatically with retry logic
export async function refreshToken(
  minValidity = 110,
  options?: { silent?: boolean; retries?: number },
): Promise<boolean | undefined> {
  const retries = options?.retries ?? 2;
  const silent = options?.silent ?? false;

  // Prevent concurrent refresh attempts
  if (isRefreshing) {
    return false;
  }

  try {
    isRefreshing = true;
    const refreshed = await keycloak?.updateToken(minValidity);
    if (refreshed) {
      // Restart expiration tracking
      startTokenExpirationTracking();
    }
    return refreshed;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);

    // Don't retry on auth errors (expired refresh token, invalid session)
    const isAuthError =
      errorMessage.includes("invalid_grant") ||
      errorMessage.includes("Session not active") ||
      errorMessage.includes("Refresh token expired");

    if (isAuthError) {
      console.error("❌ Session expired - refresh token invalid:", errorMessage);
      console.log("🔄 Logging out and redirecting to login...");
      await logOut();
      return false;
    }

    // Retry only on network failures (connection errors, 5xx, timeouts).
    // Reset isRefreshing before the recursive call — the guard at the top of
    // refreshToken checks it synchronously, and finally hasn't run yet at the
    // point the recursive call is made.
    if (retries > 0) {
      isRefreshing = false;
      await new Promise((resolve) => setTimeout(resolve, 1000)); // Wait 1s before retry
      return refreshToken(minValidity, { silent, retries: retries - 1 });
    }

    // All retries exhausted - log out user
    console.error("⛔ All token refresh retries exhausted. Logging out...");
    await logOut();
    return false;
  } finally {
    isRefreshing = false;
  }
}

// Log in function
export async function logIn(redirectUri?: string) {
  try {
    const finalRedirectUri = redirectUri ?? getBaseUrl();
    await keycloak?.login({ redirectUri: finalRedirectUri });
  } catch (error) {
    console.error("Login failed:", error);
  }
}

// Log out function
export async function logOut(redirectUri?: string) {
  // Prevent concurrent logout calls (e.g. expiration tracker and activity
  // tracker both firing at the same time). Only the first call does the work;
  // the second would see isAuthenticated=false and skip keycloak.logout().
  if (isLoggingOut) return;
  isLoggingOut = true;

  try {
    // Clean up state BEFORE Keycloak redirect (which may navigate away)
    const wasAuthenticated = isAuthenticated;
    isAuthenticated = false;
    isRefreshing = false;
    stopTokenExpirationTracking();
    stopAutoTokenRefresh();
    stopActivityTracking();

    if (typeof window !== "undefined") {
      localStorage.clear();
      sessionStorage.clear();
    }

    // Synchronously notify the Nuxt app to reset its reactive auth state.
    // This makes the auth middleware treat the user as unauthenticated on the
    // next SPA navigation — critical if keycloak.logout() fails to redirect.
    logoutCallback?.();

    const finalRedirectUri = redirectUri ?? getBaseUrl() + "/";
    if (keycloak && wasAuthenticated) {
      await keycloak.logout({ redirectUri: finalRedirectUri });
      // keycloak.logout() calls window.location.replace() internally and returns.
      // Do NOT set window.location.href here — it would race with and override
      // the Keycloak logout navigation, sending the browser straight back to the
      // app before the server-side session is invalidated.
    }
  } catch (error) {
    console.error("Logout failed:", error);
    if (typeof window !== "undefined") {
      window.location.href = getBaseUrl() + "/";
    }
  } finally {
    isLoggingOut = false;
  }
}

export function getBaseUrl(): string {
  if (typeof window === "undefined") {
    return "";
  }

  // Try to get from runtime config first
  const baseUrl = window.__NUXT__?.config?.public?.APP_BASE_URL;
  if (baseUrl && baseUrl !== "/") {
    return window.location.origin + baseUrl;
  }

  return window.location.origin;
}

// Get user roles
export function getRoles(): string[] {
  return keycloak?.realmAccess?.roles || [];
}

// Get user info
export async function getUserInfo() {
  try {
    return await keycloak?.loadUserInfo();
  } catch (error) {
    console.error("Failed to load user info:", error);
    return null;
  }
}

// Start token expiration tracking (updates UI countdowns)
function startTokenExpirationTracking() {
  // Clear existing timers if any
  stopTokenExpirationTracking();

  const updateExpirationTimes = async () => {
    const currentTime = Math.floor(Date.now() / 1000);

    // Access Token Expiration
    if (keycloak?.tokenParsed?.exp) {
      const accessTokenExpiresIn = keycloak.tokenParsed.exp - currentTime;
      accessTokenExpiration.value = accessTokenExpiresIn > 0 ? accessTokenExpiresIn : 0;

      const accessMinutes = Math.floor(accessTokenExpiration.value / 60);
      const accessSeconds = accessTokenExpiration.value % 60;
      accessTokenTimeLeft.value = `${`0${accessMinutes}`.slice(-2)}:${`0${accessSeconds}`.slice(-2)}`;
    } else {
      accessTokenExpiration.value = null;
      accessTokenTimeLeft.value = "Expired!";
    }

    // Refresh Token Expiration
    if (keycloak?.refreshToken) {
      const refreshPayload = JSON.parse(atob(keycloak.refreshToken?.split(".")[1] || ""));
      const refreshTokenExpiresIn = refreshPayload.exp - currentTime;
      refreshTokenExpiration.value = refreshTokenExpiresIn > 0 ? refreshTokenExpiresIn : 0;

      const refreshMinutes = Math.floor(refreshTokenExpiration.value / 60);
      const refreshSeconds = refreshTokenExpiration.value % 60;
      refreshTokenTimeLeft.value = `${`0${refreshMinutes}`.slice(-2)}:${`0${refreshSeconds}`.slice(-2)}`;

      // If refresh token is expired, log out.
      // await ensures the isLoggingOut guard in logOut() works correctly and
      // that the interval is cleared before any redirect fires.
      if (refreshTokenExpiresIn <= 0) {
        console.error("⛔ Refresh Token expired, logging out...");
        await logOut();
      }
    } else {
      refreshTokenExpiration.value = null;
      refreshTokenTimeLeft.value = "N/A";
    }
  };

  // Run immediately, then every second.
  // void-cast the promise — errors are handled inside updateExpirationTimes.
  void updateExpirationTimes();
  tokenCheckInterval = setInterval(() => {
    void updateExpirationTimes();
  }, 1000);
}

// Start auto-refreshing the Access Token
function startAutoTokenRefresh() {
  if (!autoRefreshEnabled) {
    return;
  }

  // Clear any existing intervals
  stopAutoTokenRefresh();

  autoRefreshInterval = setInterval(async () => {
    if (keycloak?.tokenParsed?.exp) {
      const currentTime = Math.floor(Date.now() / 1000);
      const timeUntilExpiration = keycloak.tokenParsed.exp - currentTime;

      if (timeUntilExpiration < autoRefreshTimeLeft) {
        console.log(`🔄 Access Token expires in ${timeUntilExpiration}s, refreshing...`);
        await refreshToken();
      }
    }
  }, autoRefreshCheckInterval); // Use configured check interval
}

// Stop tracking expiration times when logging out
function stopTokenExpirationTracking() {
  if (tokenCheckInterval) {
    clearInterval(tokenCheckInterval);
    tokenCheckInterval = null;
  }
}

// Stop auto-refresh when logging out
function stopAutoTokenRefresh() {
  if (autoRefreshInterval) {
    clearInterval(autoRefreshInterval);
    autoRefreshInterval = null;
  }
}

// Start activity tracking
function startActivityTracking() {
  if (!activityTrackingEnabled || activityTracking) {
    return;
  }

  // Callback for inactivity timeout
  const handleInactivityTimeout = async () => {
    console.warn("⏱️ Session expired due to inactivity. Logging out...");
    await logOut();
  };

  // Get inactivity timeout from config (or use default)
  const config = useRuntimeConfig();
  const inactivityTimeout = config.public.OIDC.inactivityTimeout;

  activityTracking = useActivityTracking(refreshToken, handleInactivityTimeout, {
    enabled: true,
    checkIntervalMs: autoRefreshCheckInterval,
    refreshThresholdSeconds: autoRefreshTimeLeft,
    inactivityTimeoutMinutes: inactivityTimeout,
  });

  activityTracking.startTracking();
}

// Stop activity tracking
function stopActivityTracking() {
  if (activityTracking) {
    activityTracking.stopTracking();
    activityTracking = null;
  }
}

// Export for use in BaseClient to track API calls
export function trackApiActivity() {
  if (activityTracking) {
    activityTracking.trackActivity();
  }
}
